import type { Booking, BookingsStore, CartStore, ProductAvailabilityStore, Product } from './types';

export type AddToCartParams = {
	product: Product | undefined;
	isLoading: boolean;
	dateRangeStart?: Date | null;
	dateRangeEnd?: Date | null;
	propertyContent: boolean;
	hasActiveBookingDateRange: boolean;
	activeBooking: Booking | undefined;
	bookingsForProperty: Booking[];
	hasDates: boolean;
	hasDatesAndAvailability: boolean;
};

/**
 * Ported from the legacy `getAddToCartParams` util - the single place that reads the mobx stores and
 * derives the widget's top-level state. Not a hook (no internal state), just called directly on every
 * render like the original, which relied on mobx's `observer` for reactivity.
 */
export default function useAddToCartParams({
	bookingsStore,
	cartStore,
	productAvailabilityStore,
	productId,
}: {
	bookingsStore: BookingsStore;
	cartStore: CartStore;
	productAvailabilityStore: ProductAvailabilityStore;
	productId: string;
}): AddToCartParams {
	const isLoading = productAvailabilityStore.fetching;
	const dateRangeStart = productAvailabilityStore?.dateRange?.start;
	const dateRangeEnd = productAvailabilityStore?.dateRange?.end;

	const bookingsForProperty = (bookingsStore.bookingsForProperty || []) as unknown as Booking[];
	const activeBooking = (cartStore.bookingForProperty || bookingsStore.activeBooking) as unknown as
		| Booking
		| undefined;

	const hasActiveBookingDateRange = Boolean(activeBooking?.hotelProducts?.[0]?.checkInDate);
	const product = productAvailabilityStore.allProducts.find(
		({ id }: { id: string }) => id === productId
	) as unknown as Product | undefined;
	const propertyContent = productAvailabilityStore.propertyContent;
	const hasAvailability = Boolean(
		product?.id && productAvailabilityStore.productAvailabilityMap[product.id]
	);
	const hasDates = Boolean(
		productAvailabilityStore.dateRange.start && productAvailabilityStore.dateRange.end
	);

	const hasDatesAndAvailability = hasDates && hasAvailability;

	return {
		product,
		isLoading,
		dateRangeStart,
		dateRangeEnd,
		propertyContent,
		hasActiveBookingDateRange,
		activeBooking,
		bookingsForProperty,
		hasDates,
		hasDatesAndAvailability,
	};
}
